import numeral from 'numeral';
import moment from 'moment';
import PluginZoom from 'chartjs-plugin-zoom';
import * as appColors from '@/functions/appColors';
import tooltipTitle from '@/functions/chartTooltipDate';

export const container = data => ({
    type: 'mixed',
    data,
    plugins: [PluginZoom],
    options: {
        legend: {
            display: true,
            labels: {
                // because we split the datasets into two, we would get two sets of labels for each
                // dataset.
                // So: only show every second label in the legend.
                filter: label => (label.datasetIndex % 2 !== 0),
            },
        },
        responsive: true,
        maintainAspectRatio: false,
        tooltips: {
            mode: 'x',
            intersect: false,
            position: 'nearest',
            callbacks: {
                label(tooltipItem, d) {
                    const label = d.datasets[tooltipItem.datasetIndex].label || '';
                    const value = numeral(tooltipItem.yLabel).format('0,0.[00]');
                    return `${label}: ${value} mm`;
                },
                title: tooltipTitle,
            },
        },
        spanGaps: true,
        annotation: {
            events: [],
            annotations: [
                {
                    drawTime: 'afterDatasetsDraw',
                    id: 'vline',
                    type: 'line',
                    mode: 'vertical',
                    scaleID: 'x-axis-0',
                    value: moment(),
                    borderColor: 'black',
                    borderWidth: 3,
                    label: {
                        backgroundColor: appColors.todayMarker,
                        content: moment().format('LL'),
                        enabled: true,
                    },
                },
            ],
        },
        pan: {
            enabled: true,
            mode: 'y',
            speed: 10,
            threshold: 10,
        },
        zoom: {
            enabled: true,
            mode: 'y',
            limits: {
                max: 100,
                min: 1,
            },
        },
        scales: {
            xAxes: [
                {
                    type: 'time',
                    distribution: 'linear',
                    time: {
                        tooltipFormat: 'MMM DD, YYYY',
                        unit: 'month',
                    },
                },
            ],
            yAxes: [
                {
                    scaleLabel: {
                        display: true,
                        labelString: 'Snow Water Equivalent (mm)',
                    },
                },
            ],
            // disable animations
            animation: false,
        },
    },
});

export const data = (label, currentData, historicalData) => {
    /**
     * in order to have a gap for July 1st to October 31st, we need to split the data
     * based on their "date of year" values, which is 63 and 183.
     * this complicates the area fill a bit, keep track of the relative values
     */
    const hdatasplit = { first: [], last: [] };
    historicalData.forEach((el, i) => {
        if (i <= 63) {
            hdatasplit.first.push(el);
        }
        if (i >= 183) {
            hdatasplit.last.push(el);
        }
    });
    const cdatasplit = { first: [], last: [] };
    currentData.forEach((el, i) => {
        if (i <= 63) {
            cdatasplit.first.push(el);
        }
        if (i >= 183) {
            cdatasplit.last.push(el);
        }
    });
    const pRadius = 4;
    const bWidth = 0;
    const lTension = 0.3;
    return {
        labels: label,
        datasets: [
            { // 0
                type: 'line',
                label: 'Current Manual Snow Survey',
                borderColor: appColors.currentBorder,
                backgroundColor: appColors.currentBg,
                borderWidth: bWidth,
                fill: false,
                pointRadius: pRadius,
                pointHitRadius: 2,
                lineTension: lTension,
                // steppedLine: 'before',
                data: cdatasplit.first.map((d) => {
                    const valObj = {
                        x: d.d,
                        y: d.v,
                    };
                    return valObj;
                }),
            },
            { // 1
                type: 'line',
                label: 'Current Manual Snow Survey',
                borderColor: appColors.currentBorder,
                backgroundColor: appColors.currentBg,
                borderWidth: bWidth,
                fill: false,
                pointRadius: pRadius,
                pointHitRadius: 2,
                lineTension: lTension,
                // steppedLine: 'before',
                data: cdatasplit.last.map((d) => {
                    const valObj = {
                        x: d.d,
                        y: d.v,
                    };
                    return valObj;
                }),
            },
            { // 2
                type: 'line',
                label: 'Historical mean',
                borderColor: appColors.historicalMean,
                backgroundColor: appColors.historicalMean,
                borderWidth: bWidth,
                fill: false,
                // steppedLine: 'before',
                pointRadius: pRadius,
                pointHitRadius: 2,
                lineTension: lTension,
                // pointHitRadius: 1,
                data: hdatasplit.first.map((d) => {
                    const valObj = {
                        x: d.d,
                        y: d.p50,
                    };
                    return valObj;
                }),
            },
            { // 3
                type: 'line',
                label: 'Historical Mean',
                borderColor: appColors.historicalMean,
                backgroundColor: appColors.historicalMean,
                borderWidth: bWidth,
                fill: false,
                // steppedLine: 'before',
                pointRadius: pRadius,
                pointHitRadius: 2,
                lineTension: lTension,
                // pointHitRadius: 1,
                data: hdatasplit.last.map((d) => {
                    const valObj = {
                        x: d.d,
                        y: d.p50,
                    };
                    return valObj;
                }),
            },
            { // 4
                type: 'line',
                label: 'Historical 75th %ile',
                borderColor: appColors.historical1,
                backgroundColor: appColors.historical1,
                borderWidth: bWidth,
                fill: '-2', // fill to 2
                // steppedLine: 'before',
                pointRadius: pRadius,
                pointHitRadius: 2,
                pointBackgroundColor: appColors.historicalMean,
                lineTension: lTension,
                // pointHitRadius: 1,
                data: hdatasplit.first.map((d) => {
                    const valObj = {
                        x: d.d,
                        y: d.p75,
                    };
                    return valObj;
                }),
            },
            { // 5
                type: 'line',
                label: 'Historical 75th %ile',
                borderColor: appColors.historical1,
                backgroundColor: appColors.historical1,
                borderWidth: bWidth,
                fill: '-2', // fill to 7
                // steppedLine: 'before',
                pointRadius: pRadius,
                pointHitRadius: 2,
                pointBackgroundColor: appColors.historicalMean,
                lineTension: lTension,
                // pointHitRadius: 1,
                data: hdatasplit.last.map((d) => {
                    const valObj = {
                        x: d.d,
                        y: d.p75,
                    };
                    return valObj;
                }),
            },
            { // 6
                type: 'line',
                label: 'Historical 25th %ile',
                borderColor: appColors.historical1,
                backgroundColor: appColors.historical1,
                borderWidth: bWidth,
                fill: '-4',
                // steppedLine: 'before',
                pointRadius: pRadius,
                pointHitRadius: 2,
                pointBackgroundColor: appColors.historicalMean,
                lineTension: lTension,
                // pointHitRadius: 1,
                data: hdatasplit.first.map((d) => {
                    const valObj = {
                        x: d.d,
                        y: d.p25,
                    };
                    return valObj;
                }),
            },
            { // 7
                type: 'line',
                label: 'Historical 25th %ile',
                borderColor: appColors.historical1,
                backgroundColor: appColors.historical1,
                borderWidth: bWidth,
                fill: '-4',
                // steppedLine: 'before',
                pointRadius: pRadius,
                pointHitRadius: 2,
                pointBackgroundColor: appColors.historicalMean,
                lineTension: lTension,
                // pointHitRadius: 1,
                data: hdatasplit.last.map((d) => {
                    const valObj = {
                        x: d.d,
                        y: d.p25,
                    };
                    return valObj;
                }),
            },
            { // 8
                type: 'line',
                label: 'Historical 90th %ile',
                borderColor: appColors.historical2,
                backgroundColor: appColors.historical2,
                borderWidth: bWidth,
                fill: '-4',
                // steppedLine: 'before',
                pointRadius: pRadius,
                pointHitRadius: 2,
                pointBackgroundColor: appColors.historical1,
                lineTension: lTension,
                // pointHitRadius: 1,
                data: hdatasplit.first.map((d) => {
                    const valObj = {
                        x: d.d,
                        y: d.p90,
                    };
                    return valObj;
                }),
            },
            { // 9
                type: 'line',
                label: 'Historical 90th %ile',
                borderColor: appColors.historical2,
                backgroundColor: appColors.historical2,
                borderWidth: bWidth,
                fill: '-4',
                // steppedLine: 'before',
                pointRadius: pRadius,
                pointHitRadius: 2,
                pointBackgroundColor: appColors.historical1,
                lineTension: lTension,
                // pointHitRadius: 1,
                data: hdatasplit.last.map((d) => {
                    const valObj = {
                        x: d.d,
                        y: d.p90,
                    };
                    return valObj;
                }),
            },
            { // 10
                type: 'line',
                label: 'Historical 10th %ile',
                borderColor: appColors.historical2,
                backgroundColor: appColors.historical2,
                borderWidth: bWidth,
                fill: '-4', // fill to 6
                // steppedLine: 'before',
                pointRadius: pRadius,
                pointHitRadius: 2,
                pointBackgroundColor: appColors.historical1,
                lineTension: lTension,
                // pointHitRadius: 1,
                data: hdatasplit.first.map((d) => {
                    const valObj = {
                        x: d.d,
                        y: d.p10,
                    };
                    return valObj;
                }),
            },
            { // 11
                type: 'line',
                label: 'Historical 10th %ile',
                borderColor: appColors.historical2,
                backgroundColor: appColors.historical2,
                borderWidth: bWidth,
                fill: '-4', // fill to 7
                // steppedLine: 'before',
                pointRadius: pRadius,
                pointHitRadius: 2,
                pointBackgroundColor: appColors.historical1,
                lineTension: lTension,
                // pointHitRadius: 1,
                data: hdatasplit.last.map((d) => {
                    const valObj = {
                        x: d.d,
                        y: d.p10,
                    };
                    return valObj;
                }),
            },
        ],
    };
};



// WEBPACK FOOTER //
// ./src/chartData/climateManualSnowSurveyChart.js